<script lang="ts" setup>
import ControlPageTab from '@/corePagesModule/components/ControlPageTab.vue';
import { useUnmanagedBusinessStore } from '@/unmanagedBusinessModule/pages/unmanagedBusinessStore';
import { storeToRefs } from 'pinia';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import { ImportFileType } from '@/coreDomainsModule/entities/unmanagedBusiness';
import FileTypeSelectButton from '../components/FileTypeSelectButton.vue';
import { i18n } from '@/i18n';
import RegisterUnmanagedBusinessModal from '../components/RegisterUnmanagedBusinessModal.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { UnmanagedBusinessService } from '../domains/services/UnmanagedBusinessService';
import Button from '@/components/input/Btn/Btn.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { onMounted } from 'vue';
import SelectInput from '@/corePagesModule/components/SelectInput.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';
import { IPagingOptions } from '@/coreDomainsModule/paging';
import { RegisterBusinessRepository } from '@/coreInfrastructureModule/Repositories/RegisterBusinessRepository';
import RegisterUpdateInfoModal from '../components/RegisterUpdateInfoModal.vue';
import RegisterLeadTimeAndEtcModal from '../components/RegisterLeadTimeAndEtcModal.vue';
import { AggregateBusinessRepository } from '@/coreInfrastructureModule/Repositories/AggregateBusinessRepository';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import { FilterModalType } from '@/corePagesModule/filterModal/filterModal';
import { assertNever } from '@/coreDomainsModule/assertNever';
import { TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';

const t = i18n.t;
const locale = i18n.locale;
const registerBusinessRepository = new RegisterBusinessRepository();
const aggregateBusinessRepository = new AggregateBusinessRepository();
const store = useUnmanagedBusinessStore();
const { state } = storeToRefs(store);
const {
  fetchUnmanagedBusinesses,
  openModal,
  closeModal,
  deleteCheckedUnmanagedBusiness,
  getTableType,
  openModalByPreviousModal,
  onAppliedFilterModal,
  sortTableContentItems,
  summaryGetFilterItemValue,
} = store;
/**
 * 年の選択肢（2014年から2034年まで）
 */
const yearOptions = Array.from({ length: 2034 - 2014 + 1 }, (_, i) => (2014 + i).toString());

/**
 * ページング変更時の処理
 * @param pagingOptions - ページングオプション
 */
const onChangePaging = async (pagingOptions: IPagingOptions) => {
  state.value.tableInfo[state.value.selectedFileType].pagingState = pagingOptions;
  await fetchUnmanagedBusinesses();
};

/**
 * フィルターモーダルの種類を取得
 * @param fileType - ファイルタイプ
 * @returns フィルターモーダルの種類
 */
const getFilterModalType = (fileType: ImportFileType): FilterModalType => {
  switch (fileType) {
    case ImportFileType.Announce:
      return 'unmanagedBusinessTypeAnnounce';
    case ImportFileType.Inventory:
      return 'unmanagedBusinessTypeInventory';
    case ImportFileType.SalesResult:
      return 'unmanagedBusinessTypeSalesResult';
    case ImportFileType.Arrival:
      return 'unmanagedBusinessTypeArrival';
    default:
      return assertNever(fileType);
  }
};

onMounted(async () => {
  await fetchUnmanagedBusinesses();
});
</script>
<template>
  <div class="unmanaged-business">
    <h1 class="unmanaged-business__title">{{ t('管理表') }}</h1>
    <div class="unmanaged-business__header">
      <ControlPageTab />
      <div class="unmanaged-business__header-filetype">
        <FileTypeSelectButton v-for="fileType in ImportFileType" :key="fileType" :file-type="fileType">
          {{ t(`ImportFileType:${fileType}`) }}
        </FileTypeSelectButton>
      </div>
      <Button
        class="unmanaged-business__header-delete-button"
        button-style="btn-primary"
        :disabled="state.checkedIds[state.selectedFileType].length === 0"
        @click="openModal('delete')"
        >{{ t('削除') }}
        <SvgLoader name="trashIcon" />
      </Button>
    </div>

    <template v-for="fileType in ImportFileType" :key="fileType">
      <div v-if="state.selectedFileType === fileType" class="unmanaged-business__table-container">
        <div class="unmanaged-business__filter-pagination-container">
          <FilterModalButton
            v-if="state.selectedFileType === fileType"
            :filter-modal-type="getFilterModalType(fileType)"
            :select-input-index="10"
            :filter-items="summaryGetFilterItemValue[fileType]().value"
            @apply="(filteredContents) => onAppliedFilterModal(filteredContents)"
          />
          <Pagination
            :paging-options="state.tableInfo[fileType].pagingState"
            :is-loading="isLoading(state.tableInfo[fileType].loadingState.status)"
            @on-change-page="onChangePaging"
          />
        </div>
        <NewBaseTableContent
          :key="fileType"
          :prop-table-content-properties="state.tableInfo[fileType].tableContentProperties"
          :prop-display-objects="state.tableInfo[fileType].tableContentItems"
          :prop-loading-status="state.tableInfo[fileType].loadingState"
          :prop-table-type="getTableType(fileType)"
          :prop-is-reset="state.tableInfo[fileType].isReset"
          class=""
          @checked="state.checkedIds[fileType] = $event"
          @on-click-sort="(sortKey: TableContentProperty, isAsc: boolean) => sortTableContentItems(sortKey, isAsc)"
          @reload="fetchUnmanagedBusinesses()"
        />
      </div>
    </template>

    <RegisterUnmanagedBusinessModal />
    <RegisterUpdateInfoModal />
    <RegisterLeadTimeAndEtcModal />

    <InfoModal
      icon="error"
      color-style="error"
      :modal-state="state.modalInfo.isOpen.delete"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('削除')"
      @on-back="closeModal('delete')"
      @on-next="deleteCheckedUnmanagedBusiness()"
    >
      <template #title>{{ t('未管理取引を削除します') }}</template>
      <template #description>
        {{ t('在庫管理していない取引を消去します。') }} <br />
        {{ t('よろしいですか？') }}
      </template>
    </InfoModal>

    <InfoModal
      icon="info"
      :modal-state="state.modalInfo.isOpen.noDuplication"
      :back-button-label="t('戻る')"
      :next-button-label="t('登録する')"
      @on-back="openModal('registerBusiness')"
      @on-next="openModalByPreviousModal('noDuplication', aggregateBusinessRepository, registerBusinessRepository)"
    >
      <template #title>{{ t('重複取引はありません') }}</template>
      <template #description>
        {{ t('重複取引が検出されなかったため、') }} <br />
        {{ t('追記した取引を登録することができます。') }}
      </template>
    </InfoModal>

    <InfoModal
      icon="info"
      :modal-state="state.modalInfo.isOpen.detectDuplication"
      :back-button-label="t('戻る')"
      :next-button-label="t('重複取引を確認')"
      @on-back="openModal('registerBusiness')"
      @on-next="openModalByPreviousModal('detectDuplication')"
    >
      <template #title>{{ t('重複取引が検出されました') }}</template>
      <template #description>
        {{ t('すでに登録されている取引が検出されました。') }} <br />
        {{ t('重複取引を確認後、既存取引へ入力値を反映することができます。') }}
      </template>
    </InfoModal>

    <InfoModal
      icon="info"
      :modal-state="state.modalInfo.isOpen.successRegister"
      :back-button-label="t('閉じる')"
      @on-back="closeModal('successRegister')"
    >
      <template #title>{{ t('取引情報を在庫表に登録しました') }}</template>
    </InfoModal>

    <InfoModal
      icon="info"
      :modal-state="state.modalInfo.isOpen.successUpdateExistBusiness"
      :back-button-label="t('閉じる')"
      @on-back="closeModal('successUpdateExistBusiness')"
    >
      <template #title>{{ t('既存取引に入力値を反映しました') }}</template>
      <template #description>
        {{ t('既存取引に内示や販売実績など') }} <br />
        {{ t('反映しました。') }} <br />
        {{ t('確認は') }}<router-link class="modal__link" to="/business">{{ t('こちら') }}</router-link
        >{{ t('のページから') }}
      </template>
    </InfoModal>

    <InfoModal :modal-state="state.modalInfo.isOpen.loading">
      <template #title>{{ t('ローディング中') }}</template>
      <template #description>
        {{ t('しばしお待ちください。') }}
      </template>
    </InfoModal>

    <InfoModal :modal-state="state.modalInfo.isOpen.error" :back-button-label="t('閉じる')" @on-back="closeModal('error')">
      <template #title>{{ t('通信上のエラーが起きました。') }}</template>
      <template #description>
        {{ t('お手数ですが、再度お試しください。') }}
      </template>
    </InfoModal>

    <InfoModal
      :modal-state="state.modalInfo.isOpen.successRegisterWithAggregate"
      :next-button-label="t('次へ')"
      @on-next="openModalByPreviousModal('successRegisterWithAggregate', aggregateBusinessRepository, registerBusinessRepository)"
    >
      <template #title>{{ t('取引を在庫表に新規登録を行います') }}</template>
      <template #description>
        {{ t('下記の箇所に新しく登録されますがよろしいですか？') }}
      </template>
      <div class="modal__body-container">
        <NewBaseTableContent
          v-if="UnmanagedBusinessService.isAggregateRow(state.selectedFileType)"
          :prop-table-content-properties="state.tableInfo.modalWhenAggregateRow.tableContentProperties"
          :prop-display-objects="state.tableInfo.modalWhenAggregateRow.tableContentItems"
          :prop-loading-status="state.tableInfo.modalWhenAggregateRow.loadingState"
          :prop-table-type="'unmanagedBusinessTypeModalWhenAggregateRow'"
          class="modal__table"
        />
        <NewBaseTableContent
          v-else
          :prop-table-content-properties="state.tableInfo.modal.tableContentProperties"
          :prop-display-objects="state.tableInfo.modal.tableContentItems"
          :prop-loading-status="state.tableInfo.modal.loadingState"
          :prop-table-type="'unmanagedBusinessTypeModal'"
          class="modal__table"
        />
      </div>
    </InfoModal>

    <InfoModal
      :modal-state="state.modalInfo.isOpen.registerAnnounceStartDateModal"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('次へ')"
      @on-back="openModal('registerBusiness')"
      @on-next="openModalByPreviousModal('registerAnnounceStartDateModal', aggregateBusinessRepository, registerBusinessRepository)"
    >
      <template #title>{{ t('内示の登録開始月を選択してください') }}</template>
      <template #description>
        {{ t('何年何月の内示から登録しますか？') }}
      </template>
      <div class="modal__body-container modal__body-container-announce">
        <div class="modal__selection modal__selection-announce">
          <SelectInput
            class="modal__selection-announce__select"
            :model-value="state.modalInfo.modalState.registerAnnounceStartDate.year"
            :index="0"
            :label="t('開始年')"
            :options="yearOptions"
            :placeholder="t('年')"
            @update:model-value="(e) => (state.modalInfo.modalState.registerAnnounceStartDate.year = e)"
          />
          <SelectInput
            class="modal__selection-announce__select"
            :model-value="state.modalInfo.modalState.registerAnnounceStartDate.month"
            :index="1"
            :label="t('開始月')"
            :options="['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']"
            :placeholder="t('月')"
            @update:model-value="(e) => (state.modalInfo.modalState.registerAnnounceStartDate.month = e)"
          />
        </div>
      </div>
    </InfoModal>

    <InfoModal
      :modal-state="state.modalInfo.isOpen.registerInventoryExecutionDateModal"
      :back-button-label="t('戻る')"
      :next-button-label="t('決定')"
      @on-back="openModal('registerBusiness')"
      @on-next="openModalByPreviousModal('registerInventoryExecutionDateModal', aggregateBusinessRepository, registerBusinessRepository)"
    >
      <template #title>{{ t('今回取り込みを行う在庫の年月日を指定してください。') }}</template>
      <div class="modal__body-container modal__body-container-inventory">
        <VueDatePicker
          v-model="state.modalInfo.modalState.inventoryExecutionDate.executionDate"
          class="modal__date-picker"
          :inline="{ input: true }"
          auto-apply
          :locale="locale"
          :placeholder="t('日付を選択')"
          :show-now-button="true"
          :now-button-label="t('現在を選択')"
        />
      </div>
    </InfoModal>

    <InfoModal
      :modal-state="state.modalInfo.isOpen.questionUpdateExistBusiness"
      :back-button-label="t('戻る')"
      :next-button-label="t('入力値を反映する')"
      @on-back="openModal('registerBusiness')"
      @on-next="openModalByPreviousModal('questionUpdateExistBusiness', aggregateBusinessRepository, registerBusinessRepository)"
    >
      <template #title>{{ t('すでに存在する取引へ入力値を反映しますか？') }}</template>
      <template #description>
        {{ t('すでに登録されていた取引へ入力された内示、在庫、販売実績、入荷の値を') }}
        {{ t('反映することができます。') }}
      </template>
      <div class="modal__body-container">
        <NewBaseTableContent
          v-if="UnmanagedBusinessService.isAggregateRow(state.selectedFileType)"
          :prop-table-content-properties="state.tableInfo.modalWhenAggregateRow.tableContentProperties"
          :prop-display-objects="state.tableInfo.modalWhenAggregateRow.tableContentItems"
          :prop-loading-status="state.tableInfo.modalWhenAggregateRow.loadingState"
          :prop-table-type="'unmanagedBusinessTypeModalWhenAggregateRow'"
          class="modal__table"
        />
        <NewBaseTableContent
          v-else
          :prop-table-content-properties="state.tableInfo.modal.tableContentProperties"
          :prop-display-objects="state.tableInfo.modal.tableContentItems"
          :prop-loading-status="state.tableInfo.modal.loadingState"
          :prop-table-type="'unmanagedBusinessTypeModal'"
          class="modal__table"
        />
      </div>
    </InfoModal>
  </div>
</template>
<style lang="scss" scoped>
.unmanaged-business {
  padding: 16px;
  display: grid;
  gap: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 31.86px;
    text-align: left;
    color: $textDefault;
  }

  &__header {
    display: grid;
    justify-content: start;
    gap: 32px;
    grid-template-columns: repeat(4, auto);

    &-filetype {
      display: grid;
      gap: 8px;
      justify-content: start;
      align-content: center;
      grid-template-columns: repeat(4, minmax(80px, auto));
    }

    &-delete-button {
      display: grid;
      gap: 7px;
      justify-content: center;
      align-content: center;
      grid-template-columns: repeat(2, auto);
      padding: 8px 13px 8px 16px;
    }
  }
  &__table-container {
    overflow: scroll;
  }

  &__filter-pagination-container {
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: space-between;
  }
}

.modal {
  &__body-container {
    background-color: $backgroundDefault;
    padding: 16px;
    border-radius: 8px;

    &-announce {
      background-color: transparent;
    }

    &-inventory {
      background-color: $backgroundDefault;
      display: grid;
      justify-content: center;
    }
  }

  &__table {
    max-height: 300px !important;
    min-height: 0;
  }

  &__link {
    color: $semanticPrimary;
    text-decoration: none;
  }

  &__selection {
    &-announce {
      display: grid;
      gap: 24px;
      grid-template-columns: repeat(2, 110px);
      justify-content: center;
      background-color: transparent;

      &__select {
        border-bottom: solid 1px $borderDefault;
      }
    }
  }

  &__date-picker {
    display: block;
    width: 400px;
  }
}
</style>
